/* global globalThis */
export const SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_KEY = 'alp_sportsnew_placement_attempt_v2';
export const SPORTSNEW_PLACEMENT_ATTEMPT_LEGACY_STORAGE_KEY = 'alp_sportsnew_placement_attempt_v1';
export const SPORTSNEW_PLACEMENT_ATTEMPT_MAX_PER_USER = 8;
export const SPORTSNEW_PLACEMENT_ATTEMPT_MAX_STORAGE_BYTES = 64 * 1024;

const defaultSessionStorage = () => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};

const userSlot = userId => {
  const number = Number(userId);
  return Number.isSafeInteger(number) && number > 0 ? String(number) : null;
};
const fingerprintSlot = fingerprint => (
  /^[0-9a-f]{64}$/i.test(String(fingerprint || '')) ? String(fingerprint).toLowerCase() : null
);
const storageError = reason => Object.assign(
  new Error('Sportsnew placement attempt storage is unavailable'),
  { code: 'SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_INVALID', reason },
);
const serializedBytes = value => new TextEncoder().encode(String(value)).byteLength;

const snapshotAttempt = attempt => ({
  version: attempt?.version,
  idempotencyKey: attempt?.idempotencyKey,
  fingerprint: attempt?.fingerprint,
  userId: attempt?.userId,
  state: attempt?.state,
  phase: attempt?.phase,
  challenge: attempt?.challenge,
  surface: attempt?.surface,
  createdAt: attempt?.createdAt,
  updatedAt: attempt?.updatedAt,
  expiresAt: attempt?.expiresAt,
  accountGeneration: attempt?.accountGeneration,
  commandFingerprint: attempt?.commandFingerprint,
  placementReceipt: attempt?.placementReceipt ?? null,
  receiptExpiresAtMs: attempt?.receiptExpiresAtMs ?? null,
});

const sortedRecord = record => Object.fromEntries(
  Object.entries(record || {}).sort(([left], [right]) => left.localeCompare(right)),
);
const canonicalEnvelope = byUser => ({
  version: 3,
  byUser: Object.fromEntries(Object.entries(byUser || {})
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([slot, value]) => [slot, {
      byFingerprint: sortedRecord(value?.byFingerprint),
    }])),
});

export const createSportsnewPlacementAttemptStore = ({ storage = defaultSessionStorage() } = {}) => {
  const readRaw = key => {
    try {
      return storage?.getItem?.(key) ?? null;
    } catch {
      throw storageError('READ_FAILED');
    }
  };
  const parseObject = (raw, { allowAbsent = true } = {}) => {
    if (raw == null || raw === '') {
      if (allowAbsent) return null;
      throw storageError('EMPTY');
    }
    if (serializedBytes(raw) > SPORTSNEW_PLACEMENT_ATTEMPT_MAX_STORAGE_BYTES) {
      throw storageError('OVERSIZE');
    }
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw storageError('SHAPE');
      }
      return parsed;
    } catch (error) {
      if (error?.code === 'SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_INVALID') throw error;
      throw storageError('CORRUPT_JSON');
    }
  };
  const writeEnvelope = envelope => {
    const canonical = canonicalEnvelope(envelope.byUser);
    const serialized = JSON.stringify(canonical);
    if (serializedBytes(serialized) > SPORTSNEW_PLACEMENT_ATTEMPT_MAX_STORAGE_BYTES) return false;
    let previous;
    try {
      previous = storage?.getItem?.(SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_KEY) ?? null;
      storage?.setItem?.(SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_KEY, serialized);
      if (storage?.getItem?.(SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_KEY) === serialized) return true;
    } catch {
      // Restore below from the exact pre-write snapshot.
    }
    try {
      if (previous == null) storage?.removeItem?.(SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_KEY);
      else storage?.setItem?.(SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_KEY, previous);
    } catch {
      // The owner blocks further allocation after any failed exact read-back.
    }
    return false;
  };

  const normalizeV3 = parsed => {
    if (parsed.version !== 3
        || !parsed.byUser || typeof parsed.byUser !== 'object' || Array.isArray(parsed.byUser)) {
      throw storageError('V3_SHAPE');
    }
    const byUser = {};
    for (const [slot, value] of Object.entries(parsed.byUser)) {
      if (!userSlot(slot) || String(Number(slot)) !== String(slot)
          || !value?.byFingerprint
          || typeof value.byFingerprint !== 'object'
          || Array.isArray(value.byFingerprint)) throw storageError('V3_SLOT_SHAPE');
      const entries = Object.entries(value.byFingerprint);
      if (entries.length > SPORTSNEW_PLACEMENT_ATTEMPT_MAX_PER_USER) {
        throw storageError('V3_SLOT_CAPACITY');
      }
      const fingerprints = {};
      for (const [fingerprint, attempt] of entries) {
        const normalizedFingerprint = fingerprintSlot(fingerprint);
        if (!normalizedFingerprint
            || normalizedFingerprint !== fingerprint
            || fingerprintSlot(attempt?.fingerprint) !== normalizedFingerprint
            || userSlot(attempt?.userId) !== slot) throw storageError('V3_ATTEMPT_SCOPE');
        fingerprints[normalizedFingerprint] = snapshotAttempt(attempt);
      }
      byUser[slot] = { byFingerprint: sortedRecord(fingerprints) };
    }
    return canonicalEnvelope(byUser);
  };

  const migrateV2 = parsed => {
    if (parsed.version !== 2
        || !parsed.byUser || typeof parsed.byUser !== 'object' || Array.isArray(parsed.byUser)) {
      throw storageError('VERSION');
    }
    const byUser = {};
    for (const [slot, attempt] of Object.entries(parsed.byUser)) {
      const fingerprint = fingerprintSlot(attempt?.fingerprint);
      if (!userSlot(slot) || String(Number(slot)) !== String(slot)
          || userSlot(attempt?.userId) !== slot || !fingerprint) {
        throw storageError('V2_ATTEMPT_SCOPE');
      }
      byUser[slot] = { byFingerprint: { [fingerprint]: snapshotAttempt(attempt) } };
    }
    const migrated = canonicalEnvelope(byUser);
    if (!writeEnvelope(migrated)) throw storageError('V2_MIGRATION_WRITE');
    const exact = normalizeV3(parseObject(readRaw(SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_KEY), {
      allowAbsent: false,
    }));
    if (JSON.stringify(exact) !== JSON.stringify(migrated)) {
      throw storageError('V2_MIGRATION_READBACK');
    }
    return exact;
  };

  const readEnvelope = () => {
    const raw = readRaw(SPORTSNEW_PLACEMENT_ATTEMPT_STORAGE_KEY);
    if (raw == null || raw === '') return canonicalEnvelope({});
    const parsed = parseObject(raw, { allowAbsent: false });
    if (parsed.version === 3) return normalizeV3(parsed);
    return migrateV2(parsed);
  };
  const removeLegacyExact = expectedRaw => {
    try {
      if (storage?.getItem?.(SPORTSNEW_PLACEMENT_ATTEMPT_LEGACY_STORAGE_KEY) !== expectedRaw) {
        return false;
      }
      storage?.removeItem?.(SPORTSNEW_PLACEMENT_ATTEMPT_LEGACY_STORAGE_KEY);
      return storage?.getItem?.(SPORTSNEW_PLACEMENT_ATTEMPT_LEGACY_STORAGE_KEY) == null;
    } catch {
      return false;
    }
  };

  const migrateOwnedLegacy = (userId, commandFingerprint = null) => {
    const slot = userSlot(userId);
    if (!slot) return null;
    const legacyRaw = readRaw(SPORTSNEW_PLACEMENT_ATTEMPT_LEGACY_STORAGE_KEY);
    if (legacyRaw == null || legacyRaw === '') return null;
    const legacy = parseObject(legacyRaw, { allowAbsent: false });
    const fingerprint = fingerprintSlot(legacy.fingerprint);
    const ownedSlot = userSlot(legacy.userId);
    if (!fingerprint
        || (ownedSlot != null && ownedSlot !== slot)
        || (ownedSlot == null && fingerprint !== fingerprintSlot(commandFingerprint))) return null;
    const envelope = readEnvelope();
    const current = envelope.byUser[slot]?.byFingerprint ?? {};
    if (Object.keys(current).length >= SPORTSNEW_PLACEMENT_ATTEMPT_MAX_PER_USER
        && !current[fingerprint]) return null;
    const migrated = snapshotAttempt({ ...legacy, userId: Number(slot) });
    const next = canonicalEnvelope({
      ...envelope.byUser,
      [slot]: { byFingerprint: { ...current, [fingerprint]: migrated } },
    });
    if (!writeEnvelope(next)) return null;
    const exact = readEnvelope().byUser[slot]?.byFingerprint?.[fingerprint] ?? null;
    if (JSON.stringify(exact) !== JSON.stringify(migrated)) return null;
    removeLegacyExact(legacyRaw);
    return migrated;
  };

  const api = {
    readAll(userId) {
      const slot = userSlot(userId);
      if (!slot) return [];
      let envelope = readEnvelope();
      if (!envelope.byUser[slot]) {
        migrateOwnedLegacy(userId);
        envelope = readEnvelope();
      }
      return Object.values(envelope.byUser[slot]?.byFingerprint ?? {})
        .map(snapshotAttempt);
    },
    read(userId, fingerprint = null) {
      const slot = userSlot(userId);
      if (!slot) return null;
      const envelope = readEnvelope();
      const byFingerprint = envelope.byUser[slot]?.byFingerprint ?? {};
      const exact = fingerprintSlot(fingerprint);
      if (exact) return byFingerprint[exact] ? snapshotAttempt(byFingerprint[exact]) : null;
      const first = Object.values(byFingerprint)[0] ?? null;
      if (first) return snapshotAttempt(first);
      return migrateOwnedLegacy(userId);
    },
    claimLegacy(userId, commandFingerprint) {
      return migrateOwnedLegacy(userId, commandFingerprint);
    },
    write(attempt) {
      const slot = userSlot(attempt?.userId);
      const fingerprint = fingerprintSlot(attempt?.fingerprint);
      if (!slot || !fingerprint) return false;
      const envelope = readEnvelope();
      const current = envelope.byUser[slot]?.byFingerprint ?? {};
      if (!current[fingerprint]
          && Object.keys(current).length >= SPORTSNEW_PLACEMENT_ATTEMPT_MAX_PER_USER) return false;
      return writeEnvelope(canonicalEnvelope({
        ...envelope.byUser,
        [slot]: {
          byFingerprint: { ...current, [fingerprint]: snapshotAttempt(attempt) },
        },
      }));
    },
    remove(userId, fingerprint = null) {
      const slot = userSlot(userId);
      if (!slot) return false;
      const envelope = readEnvelope();
      const current = envelope.byUser[slot]?.byFingerprint ?? {};
      const exact = fingerprintSlot(fingerprint);
      const nextByUser = { ...envelope.byUser };
      if (exact) {
        const nextByFingerprint = { ...current };
        delete nextByFingerprint[exact];
        if (Object.keys(nextByFingerprint).length === 0) delete nextByUser[slot];
        else nextByUser[slot] = { byFingerprint: nextByFingerprint };
      } else {
        delete nextByUser[slot];
      }
      if (!writeEnvelope(canonicalEnvelope(nextByUser))) return false;
      const remaining = readEnvelope().byUser[slot]?.byFingerprint ?? {};
      return exact ? !remaining[exact] : Object.keys(remaining).length === 0;
    },
  };
  return Object.freeze(api);
};
